///////////////////////////////////////////////////////////////////////////////////////
//                          Copyright (c) 2020 - 2021
//                           Coyote Logistics L.L.C.
//                         All Rights Reserved Worldwide
//
//WARNING:  This program (or document) is unpublished, proprietary
//property of Coyote Logistics L.L.C. and is to be maintained in strict confidence.
//Unauthorized reproduction, distribution or disclosure of this program
//(or document), or any program (or document) derived from it is
//prohibited by State and Federal law, and by local law outside of the U.S.
///////////////////////////////////////////////////////////////////////////////////////

import { createSlice } from "@reduxjs/toolkit";
import {
  putSpotLegUpdate,
  postSpotLegNote,
  deleteAssignedUser,
  postSpotLegClaimed,
  postCarrierSpotLegClaim,
  postProcessSpotLegClaim,
  postRemoveSpotLegClaim,
  postAssignSpotLegWithoutClaim,
} from "../api/api";
import { fetchSpotNotes } from "./spotNotesSlice";
import { removeSpotLeg } from "./spotLegsSlice";
import types from "../helpers/types";
import { clearSpotLeg, fetchSpotLegById } from "./spotLegSlice";

export const initialState = {
  updateSpotLegRequestStatus: types.requestStatuses.initial,
  addSpotLegNoteRequestStatus: types.requestStatuses.initial,
  removeAssignedUserRequestStatus: types.requestStatuses.initial,
  claimSpotLegRequestStatus: types.requestStatuses.initial,
  processClaimRequestStatus: types.requestStatuses.initial,
  removeClaimRequestStatus: types.requestStatuses.initial,
  assignSpotLegWithoutClaimRequestStatus: types.requestStatuses.initial,
  assignSpotLegWithoutClaimRequestFailureMessage: "",
  processClaimRequestFailureMessage:""
};

const updateSpotLegSlice = createSlice({
  name: "updateSpotLegSlice",
  initialState,
  reducers: {
    requestUpdateSpotLeg: (state) => {
      state.updateSpotLegRequestStatus = types.requestStatuses.inProgress;
    },
    receiveUpdateSpotLegSuccess: (state) => {
      state.updateSpotLegRequestStatus = types.requestStatuses.success;
    },
    receiveUpdateSpotLegFailure: (state) => {
      state.updateSpotLegRequestStatus = types.requestStatuses.failure;
    },
    requestAddSpotLegNote: (state) => {
      state.addSpotLegNoteRequestStatus = types.requestStatuses.inProgress;
    },
    receiveAddSpotLegNoteSuccess: (state) => {
      state.addSpotLegNoteRequestStatus = types.requestStatuses.success;
    },
    receiveAddSpotLegNoteFailure: (state) => {
      state.addSpotLegNoteRequestStatus = types.requestStatuses.failure;
    },
    requestRemoveAssignedUser: (state) => {
      state.removeAssignedUserRequestStatus = types.requestStatuses.inProgress;
    },
    receiveRemoveAssignedUserSuccess: (state) => {
      state.removeAssignedUserRequestStatus = types.requestStatuses.success;
    },
    receiveRemoveAssignedUserFailure: (state) => {
      state.removeAssignedUserRequestStatus = types.requestStatuses.failure;
    },
    requestClaimSpotLeg: (state) => {
      state.claimSpotLegRequestStatus = types.requestStatuses.inProgress;
    },
    receiveClaimSpotLegSuccess: (state) => {
      state.claimSpotLegRequestStatus = types.requestStatuses.success;
    },
    receiveClaimSpotLegFailure: (state) => {
      state.claimSpotLegRequestStatus = types.requestStatuses.failure;
    },
    requestProcessSpotLegClaim: (state) => {
      state.processClaimRequestStatus = types.requestStatuses.inProgress;
    },
    receiveProcessSpotLegClaimSuccess: (state) => {
      state.processClaimRequestStatus = types.requestStatuses.success;
    },
    receiveProcessSpotLegClaimFailure: (state, {payload}) => {
      state.processClaimRequestStatus = types.requestStatuses.failure;
      state.processClaimRequestFailureMessage = payload;
    },
    requestRemoveSpotLegClaim: (state) => {
      state.removeClaimRequestStatus = types.requestStatuses.inProgress;
    },
    receiveRemoveSpotLegClaimSuccess: (state) => {
      state.removeClaimRequestStatus = types.requestStatuses.success;
    },
    receiveRemoveSpotLegClaimFailure: (state) => {
      state.removeClaimRequestStatus = types.requestStatuses.failure;
    },
    requestAssignSpotLegWithoutClaim: (state) => {
      state.assignSpotLegWithoutClaimRequestStatus =
        types.requestStatuses.inProgress;
    },
    receiveAssignSpotLegWithoutClaimSuccess: (state) => {
      state.assignSpotLegWithoutClaimRequestStatus =
        types.requestStatuses.success;
    },
    receiveAssignSpotLegWithoutClaimFailure: (state, {payload}) => {
      state.assignSpotLegWithoutClaimRequestStatus =
        types.requestStatuses.failure;
      state.assignSpotLegWithoutClaimRequestFailureMessage = payload;
    },
  },
});

export const {
  requestUpdateSpotLeg,
  receiveUpdateSpotLegSuccess,
  receiveUpdateSpotLegFailure,
  requestAddSpotLegNote,
  receiveAddSpotLegNoteSuccess,
  receiveAddSpotLegNoteFailure,
  requestRemoveAssignedUser,
  receiveRemoveAssignedUserSuccess,
  receiveRemoveAssignedUserFailure,
  requestClaimSpotLeg,
  receiveClaimSpotLegSuccess,
  receiveClaimSpotLegFailure,
  requestProcessSpotLegClaim,
  receiveProcessSpotLegClaimSuccess,
  receiveProcessSpotLegClaimFailure,
  requestRemoveSpotLegClaim,
  receiveRemoveSpotLegClaimSuccess,
  receiveRemoveSpotLegClaimFailure,
  requestAssignSpotLegWithoutClaim,
  receiveAssignSpotLegWithoutClaimSuccess,
  receiveAssignSpotLegWithoutClaimFailure,
  assignSpotLegWithoutClaimRequestFailureMessage,
  processClaimRequestFailureMessage,
} = updateSpotLegSlice.actions;

export const updateSpotLeg =
  (updates, triggerNotification) => async (dispatch) => {
    dispatch(requestUpdateSpotLeg());
    try {
      await putSpotLegUpdate(updates);
    } catch (e) {
      if (e.response.status === 403) {
        dispatch(removeSpotLeg(updates.spotLegsId));
        dispatch(clearSpotLeg());
        triggerNotification(types.notifications.legUnavailable);
      }
      dispatch(receiveUpdateSpotLegFailure());
      return;
    }
    dispatch(receiveUpdateSpotLegSuccess());
    dispatch(fetchSpotLegById(updates.spotLegsId));
    dispatch(fetchSpotNotes(updates.spotLegsId, triggerNotification));
    triggerNotification(types.notifications.routeUpdated);
  };

export const addSpotLegNote =
  (spotLegId, note, triggerNotification) => async (dispatch) => {
    dispatch(requestAddSpotLegNote());
    try {
      await postSpotLegNote(spotLegId, note);
    } catch (e) {
      if (e.response.status === 403) {
        dispatch(removeSpotLeg(spotLegId));
        dispatch(clearSpotLeg());
        triggerNotification(types.notifications.legUnavailable);
      }
      dispatch(receiveAddSpotLegNoteFailure());
      return;
    }

    dispatch(receiveAddSpotLegNoteSuccess());
    dispatch(fetchSpotNotes(spotLegId));
    triggerNotification(types.notifications.legUpdated);
  };

export const removeAssignedUser =
  (spotLegId, triggerNotification) => async (dispatch) => {
    dispatch(requestRemoveAssignedUser());
    try {
      await deleteAssignedUser(spotLegId);
    } catch (e) {
      dispatch(receiveRemoveAssignedUserFailure());
      return;
    }
    dispatch(receiveRemoveAssignedUserSuccess());
    triggerNotification(types.notifications.legUpdated);
  };

export const claimSpotLeg =
  (
    spotLegId,
    triggerNotification,
    spotLegDetails = null,
    setLegToClaimed = null
  ) =>
  async (dispatch, getState) => {
    dispatch(requestClaimSpotLeg());

    try {
      await postCarrierSpotLegClaim(spotLegId, spotLegDetails);
    } catch (e) {
      if (e.response.status === 400) {
        dispatch(removeSpotLeg(spotLegId));
        triggerNotification(types.notifications.legUnavailable);
      }
      dispatch(receiveClaimSpotLegFailure());
      return;
    }

    dispatch(receiveClaimSpotLegSuccess());
    dispatch(removeSpotLeg(spotLegId));
    triggerNotification(types.notifications.routeClaimed);
    setLegToClaimed(true);
  };

export const processSpotLegClaim =
  (claimsId, params, spotLegId, handleNotification, handleModalClose = null) =>
  async (dispatch) => {
    const notification = params.accepted
      ? types.notifications.routeUpdated
      : types.notifications.claimRejected;
    dispatch(requestProcessSpotLegClaim());
    try {
      await postProcessSpotLegClaim(claimsId, params);
    } catch (e) {
      dispatch(receiveProcessSpotLegClaimFailure(e.response?.data));
      return;
    }
    dispatch(receiveProcessSpotLegClaimSuccess());
    if (!params.accepted) {
      handleModalClose();
    }
    dispatch(fetchSpotLegById(spotLegId, handleNotification));
    handleNotification(notification);
  };

export const removeSpotLegClaim =
  (spotLegId, setClaimAlreadyProcessedModalOpen) => async (dispatch) => {
    dispatch(requestRemoveSpotLegClaim());
    try {
      await postRemoveSpotLegClaim(spotLegId);
    } catch (e) {
      if (e.response?.status === 409) {
        setClaimAlreadyProcessedModalOpen(true);
      }
      dispatch(receiveRemoveSpotLegClaimFailure());
      return;
    }
    dispatch(fetchSpotLegById(spotLegId));
    dispatch(receiveRemoveSpotLegClaimSuccess());
  };

export const assignSpotLegWithoutClaim =
  (spotLegId, carrierId, handleNotification) => async (dispatch) => {
    dispatch(requestAssignSpotLegWithoutClaim());
    try {
      await postAssignSpotLegWithoutClaim(spotLegId, carrierId);
    } catch(e) {
      //need a to come up with a better way to return error types and their desc
      const errorMessage = e.response?.data?.includes("already claimed")?
      "This leg has been claimed. Please accept or reject the Carrier's claim to proceed.":
      e.response?.data;
      dispatch(receiveAssignSpotLegWithoutClaimFailure(errorMessage));
      return;
    }
    dispatch(receiveAssignSpotLegWithoutClaimSuccess());
    dispatch(fetchSpotLegById(spotLegId, handleNotification));
    handleNotification(types.notifications.routeUpdated);
  };

export const updateSpotLegReducer = updateSpotLegSlice.reducer;